import type {
  AccountSeatOverview,
  AccountUserRole,
  PersonSeatAssignment,
} from "@/domain/model/AccountSeatOverview";
import type { ChangeAccountUserRole } from "@/domain/usecase/ChangeAccountUserRole";
import type { GetAccountSeats } from "@/domain/usecase/GetAccountSeats";
import type { GrantPersonAccess } from "@/domain/usecase/GrantPersonAccess";
import type { InviteAccountUser } from "@/domain/usecase/InviteAccountUser";
import type { RemovePersonSeat } from "@/domain/usecase/RemovePersonSeat";
import type { ResendAccountInvitation } from "@/domain/usecase/ResendAccountInvitation";
import type { RestorePersonSeat } from "@/domain/usecase/RestorePersonSeat";
import type { RevokeInvitation } from "@/domain/usecase/RevokeInvitation";
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";

type ActionKind =
  | "invite"
  | "grantAccess"
  | "removeSeat"
  | "restoreSeat"
  | "resendInvitation"
  | "revokeInvitation"
  | "changeRole"
  | null;

interface InviteDialogState {
  open: boolean;
  email: string;
  role: AccountUserRole;
}

interface GrantAccessDialogState {
  open: boolean;
  personId: string | null;
  email: string;
  role: AccountUserRole;
}

interface ChangeRoleDialogState {
  open: boolean;
  personId: string | null;
  userId: string | null;
  displayName: string;
  currentRole: AccountUserRole | null;
  desiredRole: AccountUserRole;
}

export interface SeatsViewModelState {
  data: AccountSeatOverview | null;
  isInitialLoading: boolean;
  isRefreshing: boolean;
  loadError: string | null;
  actionError: string | null;
  successMessage: string | null;
  activeAction: ActionKind;
  activePersonId: string | null;
  activeInvitationId: string | null;
  resendCooldowns: Record<string, number>;
  inviteDialog: InviteDialogState;
  grantAccessDialog: GrantAccessDialogState;
  changeRoleDialog: ChangeRoleDialogState;
}

type Action =
  | { type: "LOAD_START" }
  | { type: "LOAD_SUCCESS"; data: AccountSeatOverview }
  | { type: "LOAD_ERROR"; message: string }
  | { type: "REFRESH_START" }
  | {
    type: "REFRESH_SUCCESS";
    data: AccountSeatOverview;
    successMessage?: string | null;
  }
  | { type: "REFRESH_ERROR"; message: string }
  | {
    type: "ACTION_START";
    action: Exclude<ActionKind, null>;
    personId?: string | null;
    invitationId?: string | null;
  }
  | { type: "ACTION_ERROR"; message: string }
  | { type: "CLEAR_MESSAGES" }
  | { type: "SET_RESEND_COOLDOWN"; invitationId: string; until: number }
  | { type: "OPEN_INVITE_DIALOG" }
  | { type: "CLOSE_INVITE_DIALOG" }
  | { type: "SET_INVITE_EMAIL"; value: string }
  | { type: "SET_INVITE_ROLE"; value: AccountUserRole }
  | {
    type: "OPEN_GRANT_ACCESS_DIALOG";
    personId: string;
    email: string;
    role: AccountUserRole;
  }
  | { type: "CLOSE_GRANT_ACCESS_DIALOG" }
  | { type: "SET_GRANT_ACCESS_EMAIL"; value: string }
  | { type: "SET_GRANT_ACCESS_ROLE"; value: AccountUserRole }
  | {
    type: "OPEN_CHANGE_ROLE_DIALOG";
    personId: string;
    userId: string;
    displayName: string;
    currentRole: AccountUserRole;
    desiredRole: AccountUserRole;
  }
  | { type: "CLOSE_CHANGE_ROLE_DIALOG" }
  | { type: "SET_CHANGE_ROLE"; value: AccountUserRole };

const initialState: SeatsViewModelState = {
  data: null,
  isInitialLoading: true,
  isRefreshing: false,
  loadError: null,
  actionError: null,
  successMessage: null,
  activeAction: null,
  activePersonId: null,
  activeInvitationId: null,
  resendCooldowns: {},
  inviteDialog: {
    open: false,
    email: "",
    role: "WORKER",
  },
  grantAccessDialog: {
    open: false,
    personId: null,
    email: "",
    role: "WORKER",
  },
  changeRoleDialog: {
    open: false,
    personId: null,
    userId: null,
    displayName: "",
    currentRole: null,
    desiredRole: "WORKER",
  },
};

function reducer(
  state: SeatsViewModelState,
  action: Action,
): SeatsViewModelState {
  switch (action.type) {
    case "LOAD_START":
      return {
        ...state,
        isInitialLoading: true,
        loadError: null,
      };

    case "LOAD_SUCCESS":
      return {
        ...state,
        data: action.data,
        isInitialLoading: false,
        isRefreshing: false,
        loadError: null,
        actionError: null,
        activeAction: null,
        activePersonId: null,
        activeInvitationId: null,
      };

    case "LOAD_ERROR":
      return {
        ...state,
        isInitialLoading: false,
        isRefreshing: false,
        loadError: action.message,
      };

    case "REFRESH_START":
      return {
        ...state,
        isRefreshing: true,
        actionError: null,
        successMessage: null,
      };

    case "REFRESH_SUCCESS":
      return {
        ...state,
        data: action.data,
        isRefreshing: false,
        actionError: null,
        activeAction: null,
        activePersonId: null,
        activeInvitationId: null,
        successMessage: action.successMessage ?? null,
        inviteDialog: {
          ...state.inviteDialog,
          open: false,
          email: "",
          role: "WORKER",
        },
        grantAccessDialog: {
          ...state.grantAccessDialog,
          open: false,
          personId: null,
          email: "",
          role: "WORKER",
        },
        changeRoleDialog: {
          open: false,
          personId: null,
          userId: null,
          displayName: "",
          currentRole: null,
          desiredRole: "WORKER",
        },
      };

    case "REFRESH_ERROR":
      return {
        ...state,
        isRefreshing: false,
        activeAction: null,
        activePersonId: null,
        activeInvitationId: null,
        actionError: action.message,
      };

    case "ACTION_START":
      return {
        ...state,
        activeAction: action.action,
        activePersonId: action.personId ?? null,
        activeInvitationId: action.invitationId ?? null,
        actionError: null,
        successMessage: null,
      };

    case "ACTION_ERROR":
      return {
        ...state,
        activeAction: null,
        activePersonId: null,
        activeInvitationId: null,
        isRefreshing: false,
        actionError: action.message,
      };

    case "CLEAR_MESSAGES":
      return {
        ...state,
        actionError: null,
        successMessage: null,
      };

    case "OPEN_INVITE_DIALOG":
      return {
        ...state,
        inviteDialog: {
          ...state.inviteDialog,
          open: true,
        },
        actionError: null,
        successMessage: null,
      };

    case "CLOSE_INVITE_DIALOG":
      return {
        ...state,
        inviteDialog: {
          open: false,
          email: "",
          role: "WORKER",
        },
      };

    case "SET_INVITE_EMAIL":
      return {
        ...state,
        inviteDialog: {
          ...state.inviteDialog,
          email: action.value,
        },
      };

    case "SET_INVITE_ROLE":
      return {
        ...state,
        inviteDialog: {
          ...state.inviteDialog,
          role: action.value,
        },
      };

    case "OPEN_GRANT_ACCESS_DIALOG":
      return {
        ...state,
        grantAccessDialog: {
          open: true,
          personId: action.personId,
          email: action.email,
          role: action.role,
        },
        actionError: null,
        successMessage: null,
      };

    case "CLOSE_GRANT_ACCESS_DIALOG":
      return {
        ...state,
        grantAccessDialog: {
          open: false,
          personId: null,
          email: "",
          role: "WORKER",
        },
      };

    case "SET_GRANT_ACCESS_EMAIL":
      return {
        ...state,
        grantAccessDialog: {
          ...state.grantAccessDialog,
          email: action.value,
        },
      };

    case "SET_GRANT_ACCESS_ROLE":
      return {
        ...state,
        grantAccessDialog: {
          ...state.grantAccessDialog,
          role: action.value,
        },
      };
    case "SET_RESEND_COOLDOWN":
      return {
        ...state,
        resendCooldowns: {
          ...state.resendCooldowns,
          [action.invitationId]: action.until,
        },
      };

    case "OPEN_CHANGE_ROLE_DIALOG":
      return {
        ...state,
        changeRoleDialog: {
          open: true,
          personId: action.personId,
          userId: action.userId,
          displayName: action.displayName,
          currentRole: action.currentRole,
          desiredRole: action.desiredRole,
        },
        actionError: null,
        successMessage: null,
      };

    case "CLOSE_CHANGE_ROLE_DIALOG":
      return {
        ...state,
        changeRoleDialog: {
          open: false,
          personId: null,
          userId: null,
          displayName: "",
          currentRole: null,
          desiredRole: "WORKER",
        },
      };

    case "SET_CHANGE_ROLE":
      return {
        ...state,
        changeRoleDialog: {
          ...state.changeRoleDialog,
          desiredRole: action.value,
        },
      };

    default:
      return state;
  }
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

function toUserMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return "Die Aktion konnte nicht abgeschlossen werden.";
}

interface UseSeatsViewModelParams {
  getAccountSeats: GetAccountSeats;
  inviteAccountUser: InviteAccountUser;
  grantPersonAccess: GrantPersonAccess;
  removePersonSeat: RemovePersonSeat;
  restorePersonSeat: RestorePersonSeat;
  resendAccountInvitation: ResendAccountInvitation;
  revokeInvitation: RevokeInvitation;
  changeAccountUserRole: ChangeAccountUserRole;
}

export function useSeatsViewModel({
  getAccountSeats,
  inviteAccountUser,
  grantPersonAccess,
  removePersonSeat,
  restorePersonSeat,
  resendAccountInvitation,
  revokeInvitation,
  changeAccountUserRole
}: UseSeatsViewModelParams) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const fetchControllerRef = useRef<AbortController | null>(null);
  const actionControllerRef = useRef<AbortController | null>(null);
  const fetchSequenceRef = useRef(0);

  const abortCurrentFetch = useCallback(() => {
    fetchControllerRef.current?.abort();
    fetchControllerRef.current = null;
  }, []);

  const abortCurrentAction = useCallback(() => {
    actionControllerRef.current?.abort();
    actionControllerRef.current = null;
  }, []);

  const runFetch = useCallback(
    async ({
      mode,
      successMessage,
    }: {
      mode: "initial" | "refresh";
      successMessage?: string;
    }) => {
      abortCurrentFetch();

      const controller = new AbortController();
      fetchControllerRef.current = controller;
      const sequence = ++fetchSequenceRef.current;

      if (mode === "initial") {
        dispatch({ type: "LOAD_START" });
      } else {
        dispatch({ type: "REFRESH_START" });
      }

      try {
        const data = await getAccountSeats.execute(controller.signal);

        if (controller.signal.aborted) return;
        if (fetchControllerRef.current !== controller) return;
        if (sequence !== fetchSequenceRef.current) return;

        if (mode === "initial") {
          dispatch({ type: "LOAD_SUCCESS", data });
        } else {
          dispatch({ type: "REFRESH_SUCCESS", data, successMessage });
        }
      } catch (error) {
        if (isAbortError(error)) return;
        if (fetchControllerRef.current !== controller) return;
        if (sequence !== fetchSequenceRef.current) return;

        const message = toUserMessage(error);

        if (mode === "initial") {
          dispatch({ type: "LOAD_ERROR", message });
        } else {
          dispatch({ type: "REFRESH_ERROR", message });
        }
      } finally {
        if (fetchControllerRef.current === controller) {
          fetchControllerRef.current = null;
        }
      }
    },
    [abortCurrentFetch, getAccountSeats],
  );

  useEffect(() => {
    void runFetch({ mode: "initial" });

    return () => {
      abortCurrentFetch();
      abortCurrentAction();
    };
  }, [abortCurrentAction, abortCurrentFetch, runFetch]);

  const refresh = useCallback(
    async (successMessage?: string) => {
      await runFetch({ mode: "refresh", successMessage });
    },
    [runFetch],
  );

  const runAction = useCallback(
    async (
      action: Exclude<ActionKind, null>,
      execute: (signal: AbortSignal) => Promise<void>,
      options?: {
        personId?: string;
        invitationId?: string;
      },
    ) => {
      abortCurrentAction();

      const controller = new AbortController();
      actionControllerRef.current = controller;

      dispatch({
        type: "ACTION_START",
        action,
        personId: options?.personId,
        invitationId: options?.invitationId,
      });

      try {
        await execute(controller.signal);
      } catch (error) {
        if (isAbortError(error)) return;
        if (actionControllerRef.current !== controller) return;

        dispatch({
          type: "ACTION_ERROR",
          message: toUserMessage(error),
        });
      } finally {
        if (actionControllerRef.current === controller) {
          actionControllerRef.current = null;
        }
      }
    },
    [abortCurrentAction],
  );

  const openInviteDialog = useCallback(() => {
    dispatch({ type: "OPEN_INVITE_DIALOG" });
  }, []);

  const closeInviteDialog = useCallback(() => {
    dispatch({ type: "CLOSE_INVITE_DIALOG" });
  }, []);

  const setInviteEmail = useCallback((value: string) => {
    dispatch({ type: "SET_INVITE_EMAIL", value });
  }, []);

  const setInviteRole = useCallback((value: AccountUserRole) => {
    dispatch({ type: "SET_INVITE_ROLE", value });
  }, []);

  const submitInvite = useCallback(async () => {
    const email = state.inviteDialog.email.trim();
    const role = state.inviteDialog.role;
    const freeSeats = state.data?.freeSeats ?? 0;

    if (!email) {
      dispatch({
        type: "ACTION_ERROR",
        message: "Bitte eine E-Mail-Adresse eingeben.",
      });
      return;
    }

    if (freeSeats <= 0) {
      dispatch({
        type: "ACTION_ERROR",
        message:
          "Keine freien Sitze verfügbar. Bitte zuerst die Sitzanzahl erhöhen.",
      });
      return;
    }

    await runAction("invite", async (signal) => {
      await inviteAccountUser.execute(
        {
          email,
          role,
        },
        signal,
      );
      await refresh("Einladung wurde erstellt.");
    });
  }, [
    inviteAccountUser,
    refresh,
    runAction,
    state.data?.freeSeats,
    state.inviteDialog.email,
    state.inviteDialog.role,
  ]);

  const openGrantAccessDialog = useCallback((person: PersonSeatAssignment) => {
    dispatch({
      type: "OPEN_GRANT_ACCESS_DIALOG",
      personId: person.personId,
      email: person.email ?? "",
      role: person.role ?? "WORKER",
    });
  }, []);

  const closeGrantAccessDialog = useCallback(() => {
    dispatch({ type: "CLOSE_GRANT_ACCESS_DIALOG" });
  }, []);

  const setGrantAccessEmail = useCallback((value: string) => {
    dispatch({ type: "SET_GRANT_ACCESS_EMAIL", value });
  }, []);

  const setGrantAccessRole = useCallback((value: AccountUserRole) => {
    dispatch({ type: "SET_GRANT_ACCESS_ROLE", value });
  }, []);

  const submitGrantAccess = useCallback(async () => {
    const personId = state.grantAccessDialog.personId;
    const email = state.grantAccessDialog.email.trim();
    const role = state.grantAccessDialog.role;
    const freeSeats = state.data?.freeSeats ?? 0;

    if (!personId) {
      dispatch({
        type: "ACTION_ERROR",
        message: "Keine Person ausgewählt.",
      });
      return;
    }

    if (!email) {
      dispatch({
        type: "ACTION_ERROR",
        message: "Bitte eine E-Mail-Adresse eingeben.",
      });
      return;
    }

    if (freeSeats <= 0) {
      dispatch({
        type: "ACTION_ERROR",
        message:
          "Keine freien Sitze verfügbar. Bitte zuerst die Sitzanzahl erhöhen.",
      });
      return;
    }

    await runAction(
      "grantAccess",
      async (signal) => {
        await grantPersonAccess.execute(
          {
            personId,
            email,
            role,
          },
          signal,
        );

        await refresh("Zugang wurde erstellt.");
      },
      { personId },
    );
  }, [
    grantPersonAccess,
    refresh,
    runAction,
    state.data?.freeSeats,
    state.grantAccessDialog.personId,
    state.grantAccessDialog.email,
    state.grantAccessDialog.role,
  ]);

  const removeSeat = useCallback(
    async (personId: string) => {
      await runAction(
        "removeSeat",
        async (signal) => {
          await removePersonSeat.execute({ personId }, signal);
          await refresh("Sitzzuordnung wurde entfernt.");
        },
        { personId },
      );
    },
    [removePersonSeat, refresh, runAction],
  );

  const restoreSeat = useCallback(
    async (personId: string) => {
      const freeSeats = state.data?.freeSeats ?? 0;
      if (freeSeats <= 0) {
        dispatch({
          type: "ACTION_ERROR",
          message:
            "Keine freien Sitze verfügbar. Bitte zuerst die Sitzanzahl erhöhen.",
        });
        return;
      }

      await runAction(
        "restoreSeat",
        async (signal) => {
          await restorePersonSeat.execute({ personId }, signal);
          await refresh("Sitz wurde wiederhergestellt.");
        },
        { personId },
      );
    },
    [refresh, restorePersonSeat, runAction, state.data?.freeSeats],
  );

  const clearMessages = useCallback(() => {
    dispatch({ type: "CLEAR_MESSAGES" });
  }, []);

  const resendInvitation = useCallback(
    async (params: { invitationId: string; personId?: string }) => {
      const now = Date.now();
      const cooldownUntil = state.resendCooldowns[params.invitationId] ?? 0;

      if (cooldownUntil > now) {
        const seconds = Math.ceil((cooldownUntil - now) / 1000);

        dispatch({
          type: "ACTION_ERROR",
          message: `Diese Einladung kann in ${seconds} Sekunden erneut gesendet werden.`,
        });

        return;
      }

      dispatch({
        type: "SET_RESEND_COOLDOWN",
        invitationId: params.invitationId,
        until: now + 60_000,
      });

      await runAction(
        "resendInvitation",
        async (signal) => {
          await resendAccountInvitation.execute(
            { invitationId: params.invitationId },
            signal,
          );

          await refresh("Einladung wurde erneut gesendet.");
        },
        {
          invitationId: params.invitationId,
          personId: params.personId,
        },
      );
    },
    [refresh, resendAccountInvitation, runAction, state.resendCooldowns],
  );

  const revokeInvitationIntent = useCallback(
    async (params: { invitationId: string; personId?: string }) => {
      await runAction(
        "revokeInvitation",
        async (signal) => {
          await revokeInvitation.execute(
            { invitationId: params.invitationId },
            signal,
          );

          await refresh("Einladung wurde zurückgezogen.");
        },
        {
          invitationId: params.invitationId,
          personId: params.personId,
        },
      );
    },
    [refresh, revokeInvitation, runAction],
  );

  const openChangeRoleDialog = useCallback((person: PersonSeatAssignment) => {
    if (!person.userId || !person.role) {
      dispatch({
        type: "ACTION_ERROR",
        message: "Für diese Person kann keine Rolle geändert werden.",
      });
      return;
    }

    if (person.role === "OWNER") {
      dispatch({
        type: "ACTION_ERROR",
        message: "Die Rolle eines Eigentümers kann hier nicht geändert werden.",
      });
      return;
    }

    dispatch({
      type: "OPEN_CHANGE_ROLE_DIALOG",
      personId: person.personId,
      userId: person.userId,
      displayName: person.displayName,
      currentRole: person.role,
      desiredRole: person.role,
    });
  }, []);

  const closeChangeRoleDialog = useCallback(() => {
    dispatch({ type: "CLOSE_CHANGE_ROLE_DIALOG" });
  }, []);

  const setChangeRole = useCallback((value: AccountUserRole) => {
    dispatch({ type: "SET_CHANGE_ROLE", value });
  }, []);

  const submitChangeRole = useCallback(async () => {
    const dialog = state.changeRoleDialog;

    if (!dialog.personId || !dialog.userId || !dialog.currentRole) {
      dispatch({
        type: "ACTION_ERROR",
        message: "Kein Nutzer ausgewählt.",
      });
      return;
    }

    if (dialog.currentRole === "OWNER") {
      dispatch({
        type: "ACTION_ERROR",
        message: "Die Rolle eines Eigentümers kann hier nicht geändert werden.",
      });
      return;
    }

    if (dialog.desiredRole === "OWNER") {
      dispatch({
        type: "ACTION_ERROR",
        message: "Eigentümerrechte können hier nicht vergeben werden.",
      });
      return;
    }

    if (dialog.desiredRole === dialog.currentRole) {
      dispatch({
        type: "ACTION_ERROR",
        message: "Bitte wählen Sie eine andere Rolle aus.",
      });
      return;
    }

    await runAction(
      "changeRole",
      async (signal) => {
        await changeAccountUserRole.execute(
          {
            userId: dialog.userId!,
            desiredRole: dialog.desiredRole,
          },
          signal,
        );

        await refresh("Rolle wurde geändert.");
      },
      {
        personId: dialog.personId,
      },
    );
  }, [
    changeAccountUserRole,
    refresh,
    runAction,
    state.changeRoleDialog,
  ]);


  const derived = useMemo(() => {
    const data = state.data;
    const persons = data?.persons ?? [];

    return {
      seatSummary: data
        ? {
          purchasedSeats: data.purchasedSeats,
          activeSeats: data.activeSeats,
          freeSeats: data.freeSeats,
        }
        : null,
      persons,
      pendingInvitations: data?.pendingInvitations ?? [],
      isBusy: state.isRefreshing || state.activeAction !== null,
      hasNoFreeSeats: (data?.freeSeats ?? 0) <= 0,
      isInviteBusy: state.activeAction === "invite",
      isGrantAccessBusy: state.activeAction === "grantAccess",
      isResendInvitationBusy: state.activeAction === "resendInvitation",
      isRevokeInvitationBusy: state.activeAction === "revokeInvitation",
      isChangeRoleBusy: state.activeAction === "changeRole",
      activeRowAction: state.activeAction,
      activeRowPersonId: state.activePersonId,
      activeInvitationId: state.activeInvitationId,
      resendCooldowns: state.resendCooldowns,
      canSubmitInvite:
        !state.isRefreshing &&
        state.activeAction === null &&
        state.inviteDialog.email.trim().length > 0,
      canSubmitGrantAccess:
        !state.isRefreshing &&
        state.activeAction === null &&
        state.grantAccessDialog.personId !== null &&
        state.grantAccessDialog.email.trim().length > 0,
      canSubmitChangeRole:
        !state.isRefreshing &&
        state.activeAction === null &&
        state.changeRoleDialog.userId !== null &&
        state.changeRoleDialog.currentRole !== null &&
        state.changeRoleDialog.desiredRole !==
        state.changeRoleDialog.currentRole &&
        state.changeRoleDialog.desiredRole !== "OWNER",
    };
  }, [state]);

  return {
    state,
    derived,
    intents: {
      reload: refresh,
      openInviteDialog,
      closeInviteDialog,
      setInviteEmail,
      setInviteRole,
      submitInvite,
      openGrantAccessDialog,
      closeGrantAccessDialog,
      setGrantAccessEmail,
      setGrantAccessRole,
      submitGrantAccess,
      resendInvitation,
      revokeInvitation: revokeInvitationIntent,
      openChangeRoleDialog,
      closeChangeRoleDialog,
      setChangeRole,
      submitChangeRole,
      removeSeat,
      restoreSeat,
      clearMessages,
    },
  };
}
